import React from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { isVideoUrl } from '../utils/mediaUtils';
import { MediaThumbnail } from './MediaThumbnail';

interface GalleryImage {
  id: string;
  image_url: string;
  caption: string;
}

interface ImageGalleryProps {
  images: GalleryImage[];
  onDelete?: (id: string) => void;
  isOwner?: boolean;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images, 
  onDelete,
  isOwner = false 
}) => {
  const [selectedImage, setSelectedImage] = React.useState<GalleryImage | null>(null);
  const { t } = useLanguage(); 
  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image) => (
          <div 
            key={image.id} 
            className="relative group cursor-pointer"
            onClick={() => setSelectedImage(image)}
          >
            <div className="aspect-square overflow-hidden rounded-lg">
              <MediaThumbnail
                src={image.image_url}
                alt={image.caption}
                className="w-full h-full object-cover transform transition-transform group-hover:scale-110"
              />
            </div>
            {image.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-black bg-opacity-50 text-white p-2 text-sm">
                {image.caption}
              </div>
            )}
            {isOwner && onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(image.id);
                }}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Modal for full-size image view */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="max-w-4xl w-full bg-white rounded-lg overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative">
              {isVideoUrl(selectedImage.image_url) ? (
                <video
                  src={selectedImage.image_url}
                  className="w-full h-auto"
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={selectedImage.image_url}
                  alt={selectedImage.caption}
                  className="w-full h-auto"
                />
              )}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            {selectedImage.caption && (
              <div className="p-4 bg-white">
                <p className="text-gray-800">{selectedImage.caption}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};